<template>
  <div class="stock-backtest">
    <!-- 查询表单 -->
    <div class="search-card">
      <el-form :model="searchForm" class="filter-bar" label-width="90px" size="small">
        <el-row :gutter="16">
          <el-col :span="4.8">
            <el-form-item label="股票代码">
              <el-select
                v-model="searchForm.code"
                filterable
                placeholder="请选择股票"
                @change="handleCodeChange"
              >
                <el-option
                  v-for="stock in stockList"
                  :key="stock.stock_code"
                  :label="`${stock.stock_name} (${stock.stock_code})`"
                  :value="stock.stock_code"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="4.8">
            <el-form-item label="开始日期">
              <el-date-picker
                v-model="searchForm.startDate"
                type="date"
                placeholder="选择开始日期"
                value-format="yyyy-MM-dd"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>

          <el-col :span="4.8">
            <el-form-item label="结束日期">
              <el-date-picker
                v-model="searchForm.endDate"
                type="date"
                placeholder="选择结束日期"
                value-format="yyyy-MM-dd"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>

          <el-col :span="4.8">
            <el-form-item label="布林周期">
              <el-input-number
                v-model="searchForm.period"
                :min="5"
                :max="50"
                placeholder="周期"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>

          <el-col :span="4.8">
            <el-form-item label="标准差倍数">
              <el-input-number
                v-model="searchForm.devfactor"
                :min="0.5"
                :max="5"
                :step="0.1"
                placeholder="标准差"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="4.8">
            <el-form-item label="买入阈值">
              <el-input-number
                v-model="searchForm.percentBuy"
                :min="0"
                :max="0.5"
                :step="0.05"
                placeholder="买入阈值"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>

          <el-col :span="4.8">
            <el-form-item label="卖出阈值">
              <el-input-number
                v-model="searchForm.percentSell"
                :min="0.5"
                :max="1"
                :step="0.05"
                placeholder="卖出阈值"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>

          <el-col :span="4.8">
            <el-form-item label="交易数量">
              <el-input-number
                v-model="searchForm.tradeSize"
                :min="100"
                :max="10000"
                :step="100"
                placeholder="股数"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>

          <el-col :span="4.8">
            <el-form-item label="止损比例">
              <el-input-number
                v-model="searchForm.stopLoss"
                :min="0.01"
                :max="0.5"
                :step="0.01"
                placeholder="止损"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>

          <el-col :span="4.8">
            <el-form-item label="移动止损">
              <el-input-number
                v-model="searchForm.trailingStop"
                :min="0.01"
                :max="0.3"
                :step="0.01"
                placeholder="移动止损"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="24">
            <el-form-item style="text-align: center; margin-bottom: 0;">
              <el-button
                type="primary"
                icon="el-icon-data-analysis"
                :loading="loading"
                @click="runBacktest"
              >
                运行回测
              </el-button>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <!-- 回测结果 -->
    <div v-if="backtestResult" class="result-container">
      <!-- 统计卡片 -->
      <el-row :gutter="20" class="stats-row">
        <el-col :span="4">
          <div class="stat-card">
            <div class="stat-label">初始资金</div>
            <div class="stat-value">¥{{ formatNumber(backtestResult.analysis.initial_cash) }}</div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="stat-card">
            <div class="stat-label">最终资金</div>
            <div class="stat-value" :class="getProfitClass(backtestResult.analysis.total_profit)">
              ¥{{ formatNumber(backtestResult.analysis.final_value) }}
            </div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="stat-card">
            <div class="stat-label">总盈亏</div>
            <div class="stat-value" :class="getProfitClass(backtestResult.analysis.total_profit)">
              ¥{{ formatNumber(backtestResult.analysis.total_profit) }}
            </div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="stat-card">
            <div class="stat-label">收益率</div>
            <div class="stat-value" :class="getProfitClass(backtestResult.analysis.total_profit)">
              {{ ((backtestResult.analysis.final_value / backtestResult.analysis.initial_cash - 1) * 100).toFixed(2) }}%
            </div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="stat-card">
            <div class="stat-label">夏普比率</div>
            <div class="stat-value">{{ backtestResult.analysis.sharpe_ratio?.toFixed(3) || '-' }}</div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="stat-card">
            <div class="stat-label">最大回撤</div>
            <div class="stat-value stat-danger">{{ backtestResult.analysis.max_drawdown?.toFixed(2) || '-' }}%</div>
          </div>
        </el-col>
      </el-row>

      <!-- 资金曲线 -->
      <div class="chart-card">
        <div class="card-header">
          <span>资金曲线</span>
        </div>
        <div ref="equityChart" class="chart equity-chart"></div>
      </div>

      <!-- K线图和布林线 -->
      <div class="chart-card">
        <div class="card-header">
          <span>K线图与布林线</span>
        </div>
        <div ref="klineChart" class="chart kline-chart"></div>
      </div>

      <!-- 交易统计和信号列表并列 -->
      <el-row :gutter="20" class="bottom-row">
        <el-col :span="8">
          <div class="info-card">
            <div class="card-header">
              <span>交易统计</span>
            </div>
            <div class="trade-stats">
              <div class="trade-stat-item">
                <span class="label">总交易次数</span>
                <span class="value">{{ backtestResult.analysis.total_trades }}</span>
              </div>
              <div class="trade-stat-item">
                <span class="label">盈利次数</span>
                <span class="value value-success">{{ backtestResult.analysis.won_trades }}</span>
              </div>
              <div class="trade-stat-item">
                <span class="label">亏损次数</span>
                <span class="value value-danger">{{ backtestResult.analysis.lost_trades }}</span>
              </div>
              <div class="trade-stat-item">
                <span class="label">胜率</span>
                <span class="value">
                  {{ backtestResult.analysis.total_trades > 0
                    ? ((backtestResult.analysis.won_trades / backtestResult.analysis.total_trades) * 100).toFixed(2)
                    : '-' }}%
                </span>
              </div>
            </div>
          </div>
        </el-col>

        <el-col :span="16">
          <div class="info-card">
            <div class="card-header">
              <span>交易信号</span>
            </div>
            <el-table :data="backtestResult.signals" size="small" max-height="250" class="signals-table">
              <el-table-column prop="date" label="日期" width="120" />
              <el-table-column label="类型" width="80">
                <template slot-scope="scope">
                  <el-tag :type="scope.row.type === 'buy' ? 'success' : 'danger'" size="small">
                    {{ scope.row.type === 'buy' ? '买入' : '卖出' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="reason" label="原因" min-width="120" />
              <el-table-column prop="price" label="价格" width="100" align="right">
                <template slot-scope="scope">
                  {{ scope.row.price?.toFixed(2) || '-' }}
                </template>
              </el-table-column>
              <el-table-column prop="percent_b" label="%B" width="80" align="right">
                <template slot-scope="scope">
                  {{ scope.row.percent_b?.toFixed(3) || '-' }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import * as echarts from 'echarts'

export default {
  name: 'StockBacktest',
  data() {
    return {
      searchForm: {
        code: 'sz.002959',
        startDate: '2023-01-01',
        endDate: new Date().toISOString().split('T')[0],
        period: 20,
        devfactor: 2.0,
        percentBuy: 0.2,
        percentSell: 0.8,
        tradeSize: 1000,
        stopLoss: 0.05,
        trailingStop: 0.03
      },
      stockList: [],
      loading: false,
      backtestResult: null,
      klineChart: null,
      equityChart: null
    }
  },
  watch: {
    // 监听URL参数变化，支持直接在URL输入股票代码
    '$route.query.code': {
      immediate: true,
      handler(newCode) {
        if (newCode && newCode !== this.searchForm.code) {
          this.searchForm.code = newCode
          localStorage.setItem('backtest_stock_code', newCode)
        }
      }
    },
    // 监听股票代码变化，同步URL
    'searchForm.code'(newCode) {
      if (newCode) {
        localStorage.setItem('backtest_stock_code', newCode)
        if (this.$route.query.code !== newCode) {
          this.$router.replace({ query: { code: newCode } }).catch(() => {})
        }
      }
    }
  },
  mounted() {
    this.loadStockList()
    this.restoreSelectedCode()
    // 添加窗口resize监听
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.klineChart) {
      this.klineChart.dispose()
    }
    if (this.equityChart) {
      this.equityChart.dispose()
    }
  },
  methods: {
    handleResize() {
      if (this.klineChart) {
        this.klineChart.resize()
      }
      if (this.equityChart) {
        this.equityChart.resize()
      }
    },

    async loadStockList() {
      try {
        const response = await axios.get('/stock/info/list')
        if (response.data.status === 'success') {
          this.stockList = response.data.data
        }
      } catch (error) {
        console.error('加载股票列表失败:', error)
        this.$message.error('加载股票列表失败')
      }
    },

    restoreSelectedCode() {
      // 优先从URL参数读取，其次从localStorage读取
      const urlCode = this.$route.query.code
      const savedCode = localStorage.getItem('backtest_stock_code')
      const code = urlCode || savedCode

      if (code) {
        this.searchForm.code = code
        localStorage.setItem('backtest_stock_code', code)
      }
    },

    handleCodeChange(code) {
      localStorage.setItem('backtest_stock_code', code)
      // 同步更新URL参数
      if (this.$route.query.code !== code) {
        this.$router.replace({ query: { code } }).catch(() => {})
      }
    },

    async runBacktest() {
      if (!this.searchForm.code) {
        this.$message.warning('请选择股票')
        return
      }

      this.loading = true
      try {
        const params = {
          code: this.searchForm.code,
          start_date: this.searchForm.startDate,
          end_date: this.searchForm.endDate,
          period: this.searchForm.period,
          devfactor: this.searchForm.devfactor,
          percent_b_buy: this.searchForm.percentBuy,
          percent_b_sell: this.searchForm.percentSell,
          trade_size: this.searchForm.tradeSize,
          stop_loss_pct: this.searchForm.stopLoss,
          trailing_stop: this.searchForm.trailingStop
        }

        const response = await axios.get('/backtest/bollinger', { params })

        if (response.data.status === 'success') {
          this.backtestResult = response.data.data
          console.log('回测结果:', this.backtestResult)
          this.$message.success('回测完成')
          this.$nextTick(() => {
            this.renderKlineChart()
            this.renderEquityChart()
          })
        } else {
          this.$message.error(response.data.message || '回测失败')
        }
      } catch (error) {
        console.error('回测失败:', error)
        this.$message.error(error.response?.data?.message || '回测失败')
      } finally {
        this.loading = false
      }
    },

    renderKlineChart() {
      if (!this.backtestResult || !this.$refs.klineChart) {
        console.log('K线图渲染条件不满足:', {
          hasResult: !!this.backtestResult,
          hasRef: !!this.$refs.klineChart
        })
        return
      }

      try {
        if (this.klineChart) {
          this.klineChart.dispose()
        }

        this.klineChart = echarts.init(this.$refs.klineChart)

        const dates = this.backtestResult.kline.map(item => item.date)
        const closePrices = this.backtestResult.kline.map(item => item.close)
        const midLine = this.backtestResult.bollinger.map(item => item.mid)
        const topLine = this.backtestResult.bollinger.map(item => item.top)
        const botLine = this.backtestResult.bollinger.map(item => item.bot)

        console.log('K线数据:', { klineCount: closePrices.length, bollCount: midLine.length })

        // 标记买卖点
        const buySignals = this.backtestResult.signals
          .filter(s => s.type === 'buy')
          .map(s => {
            const index = dates.indexOf(s.date)
            return { index, price: s.price }
          })

        const sellSignals = this.backtestResult.signals
          .filter(s => s.type === 'sell')
          .map(s => {
            const index = dates.indexOf(s.date)
            return { index, price: s.price }
          })

      const option = {
        backgroundColor: 'transparent',
        title: {
          text: `${this.searchForm.code} 布林线回测`,
          left: 'center',
          textStyle: { color: '#a5b4fc' }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          borderColor: 'rgba(99, 102, 241, 0.3)',
          textStyle: { color: '#e2e8f0' }
        },
        legend: {
          data: ['收盘价', '中轨', '上轨', '下轨'],
          top: 30,
          textStyle: { color: '#94a3b8' }
        },
        grid: {
          left: '10%',
          right: '8%',
          top: '15%',
          height: '60%'
        },
        xAxis: {
          type: 'category',
          data: dates,
          scale: true,
          boundaryGap: false,
          axisLine: { onZero: false, lineStyle: { color: 'rgba(148, 163, 184, 0.3)' } },
          splitLine: { show: false },
          axisLabel: { color: '#94a3b8' }
        },
        yAxis: {
          scale: true,
          splitLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.1)' } },
          axisLabel: { color: '#94a3b8' }
        },
        dataZoom: [
          { type: 'inside', start: 50, end: 100 },
          { show: true, type: 'slider', top: '90%', start: 50, end: 100 }
        ],
        series: [
          {
            name: '收盘价',
            type: 'line',
            data: closePrices,
            smooth: true,
            showSymbol: false,
            lineStyle: {
              color: '#a5b4fc',
              width: 1.5
            },
            markPoint: {
              data: [
                // 买入信号 - 绿色向上三角形
                ...buySignals.map(s => ({
                  name: '买入',
                  coord: [s.index, s.price],
                  value: '买',
                  symbol: 'triangle',
                  symbolSize: 14,
                  symbolRotate: 0,
                  itemStyle: {
                    color: '#22c55e',
                    borderColor: '#16a34a',
                    borderWidth: 1
                  },
                  label: {
                    show: false
                  }
                })),
                // 卖出信号 - 红色向下三角形
                ...sellSignals.map(s => ({
                  name: '卖出',
                  coord: [s.index, s.price],
                  value: '卖',
                  symbol: 'triangle',
                  symbolSize: 14,
                  symbolRotate: 180,
                  itemStyle: {
                    color: '#ef4444',
                    borderColor: '#dc2626',
                    borderWidth: 1
                  },
                  label: {
                    show: false
                  }
                }))
              ]
            }
          },
          {
            name: '中轨',
            type: 'line',
            data: midLine,
            smooth: true,
            lineStyle: { opacity: 0.8, color: '#f59e0b' },
            showSymbol: false
          },
          {
            name: '上轨',
            type: 'line',
            data: topLine,
            smooth: true,
            lineStyle: { opacity: 0.5, color: '#6366f1' },
            showSymbol: false
          },
          {
            name: '下轨',
            type: 'line',
            data: botLine,
            smooth: true,
            lineStyle: { opacity: 0.5, color: '#6366f1' },
            showSymbol: false
          }
        ]
      }

      this.klineChart.setOption(option)
      this.klineChart.resize()
      } catch (error) {
        console.error('K线图渲染失败:', error)
      }
    },

    renderEquityChart() {
      if (!this.backtestResult || !this.$refs.equityChart) {
        console.log('资金曲线图渲染条件不满足:', {
          hasResult: !!this.backtestResult,
          hasRef: !!this.$refs.equityChart
        })
        return
      }

      try {
        if (this.equityChart) {
          this.equityChart.dispose()
        }

        this.equityChart = echarts.init(this.$refs.equityChart)

        const dates = this.backtestResult.bollinger.map(item => item.date)
        const values = this.backtestResult.bollinger.map(item => item.value)
        const drawdowns = this.backtestResult.bollinger.map(item => -(item.drawdown || 0))  // 负值表示回撤

        console.log('资金曲线数据:', { dates: dates.length, values: values.length, drawdowns: drawdowns.length })

        const option = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          borderColor: 'rgba(99, 102, 241, 0.3)',
          textStyle: { color: '#e2e8f0' }
        },
        legend: {
          data: ['账户价值', '回撤'],
          textStyle: { color: '#94a3b8' },
          top: 5
        },
        grid: {
          left: '5%',
          right: '5%',
          top: '15%',
          bottom: '15%'
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.3)' } },
          axisLabel: { color: '#94a3b8' }
        },
        yAxis: [
          {
            type: 'value',
            name: '账户价值',
            position: 'left',
            axisLabel: {
              formatter: '¥{value}',
              color: '#94a3b8'
            },
            splitLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.1)' } }
          },
          {
            type: 'value',
            name: '回撤(%)',
            position: 'right',
            axisLabel: {
              formatter: '{value}%',
              color: '#94a3b8'
            },
            splitLine: { show: false }
          }
        ],
        dataZoom: [
          { type: 'inside', start: 0, end: 100 },
          { show: true, type: 'slider', bottom: '5%', start: 0, end: 100 }
        ],
        series: [
          {
            name: '账户价值',
            type: 'line',
            data: values,
            smooth: true,
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(99, 102, 241, 0.3)' },
                  { offset: 1, color: 'rgba(99, 102, 241, 0.05)' }
                ]
              }
            },
            lineStyle: { color: '#6366f1', width: 2 }
          },
          {
            name: '回撤',
            type: 'line',
            yAxisIndex: 1,
            data: drawdowns,
            smooth: true,
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(239, 68, 68, 0.3)' },
                  { offset: 1, color: 'rgba(239, 68, 68, 0.05)' }
                ]
              }
            },
            lineStyle: { color: '#ef4444', width: 2 }
          }
        ]
      }

      this.equityChart.setOption(option)
      this.equityChart.resize()
      } catch (error) {
        console.error('资金曲线图渲染失败:', error)
      }
    },

    formatNumber(num) {
      if (num === null || num === undefined) return '-'
      return Number(num).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    getProfitClass(profit) {
      if (!profit) return ''
      return profit > 0 ? 'stat-success' : profit < 0 ? 'stat-danger' : ''
    }
  }
}
</script>

<style scoped>
.stock-backtest {
  padding: 24px;
  min-height: calc(100vh - 64px);
}

/* 搜索表单 */
.search-card {
  margin-bottom: 24px;
  padding: 24px;
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(99, 102, 241, 0.15);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.filter-bar :deep(.el-select) {
  width: 100%;
}

.filter-bar :deep(.el-date-editor) {
  width: 100%;
}

.filter-bar :deep(.el-form-item) {
  margin-bottom: 18px;
}

.filter-bar :deep(.el-row) {
  margin-bottom: 0;
}

.filter-bar :deep(.el-row:last-child) {
  margin-bottom: 0;
}

.filter-bar :deep(.el-form-item__content) {
  text-align: left;
}

.filter-bar :deep(.el-input__inner) {
  background: rgba(15, 23, 42, 0.6) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  color: #e2e8f0 !important;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.filter-bar :deep(.el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.4) !important;
}

.filter-bar :deep(.el-input__inner):focus {
  border-color: #6366f1 !important;
}

.filter-bar :deep(.el-input-number .el-input__inner) {
  padding: 0 45px 0 12px;
}

.filter-bar :deep(.el-input-number .el-input-number__decrease),
.filter-bar :deep(.el-input-number .el-input-number__increase) {
  background: rgba(15, 23, 42, 0.8) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  color: #a5b4fc !important;
  width: 34px;
  height: 17px;
  line-height: 15px;
  right: 2px;
}

.filter-bar :deep(.el-input-number .el-input-number__decrease:hover),
.filter-bar :deep(.el-input-number .el-input-number__increase:hover) {
  color: #818cf8 !important;
  background: rgba(99, 102, 241, 0.15) !important;
}

/* 结果容器 */
.result-container {
  margin-top: 20px;
}

/* 统计卡片行 */
.stats-row {
  margin-bottom: 20px;
}

.stat-card {
  text-align: center;
  padding: 20px;
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  border: 1px solid rgba(99, 102, 241, 0.15);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.stat-label {
  font-size: 13px;
  color: #94a3b8;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 22px;
  font-weight: 600;
  color: #e2e8f0;
}

.stat-success {
  color: #22c55e;
}

.stat-danger {
  color: #ef4444;
}

/* 图表卡片 */
.chart-card {
  margin-bottom: 20px;
  padding: 20px;
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(99, 102, 241, 0.15);
}

.card-header {
  font-weight: 600;
  color: #a5b4fc;
  margin-bottom: 16px;
}

.chart {
  width: 100%;
  height: 400px;
}

.equity-chart {
  height: 300px;
}

.kline-chart {
  height: 500px;
}

/* 底部行 */
.bottom-row {
  margin-bottom: 20px;
}

.info-card {
  height: 100%;
  padding: 20px;
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(99, 102, 241, 0.15);
}

/* 交易统计 */
.trade-stats {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.trade-stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(15, 23, 42, 0.4);
  border-radius: 8px;
  border: 1px solid rgba(99, 102, 241, 0.1);
}

.trade-stat-item .label {
  color: #94a3b8;
  font-size: 14px;
}

.trade-stat-item .value {
  font-weight: 600;
  font-size: 16px;
  color: #e2e8f0;
}

.value-success {
  color: #22c55e;
}

.value-danger {
  color: #ef4444;
}

/* 信号表格 */
.signals-table {
  background: transparent;
}

.signals-table :deep(.el-table__header-wrapper) {
  background: transparent;
}

.signals-table :deep(th.el-table__cell) {
  background: rgba(15, 23, 42, 0.6) !important;
  color: #a5b4fc !important;
  font-weight: 600;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
}

.signals-table :deep(td.el-table__cell) {
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
  color: #e2e8f0;
}

.signals-table :deep(.el-table__row) {
  background: rgba(30, 41, 59, 0.3);
}

.signals-table :deep(.el-table__row:hover td) {
  background: rgba(99, 102, 241, 0.1) !important;
}

.signals-table :deep(.el-table__body tr) {
  background: transparent;
}

.signals-table :deep(.el-table__body-wrapper) {
  background: transparent;
}
</style>
